(() => {
  const name = 'glint-collection-capsules';
  if (customElements.get(name)) return;

  class GlintCollectionCapsules extends HTMLElement {
    connectedCallback() {
      if (this._connected || this._initializing) return;
      this._initializing = true;
      const generation = this._generation = (this._generation || 0) + 1;
      queueMicrotask(() => {
        if (generation !== this._generation) return;
        this._initializing = false;
        if (this.isConnected) this._connect();
      });
    }

    _connect() {
      this._connected = true;
      this._listeners = [];
      this._frame = 0;
      this._rail = this.querySelector('[data-collection-capsule-rail]');
      this._panels = [...this.querySelectorAll('[data-collection-capsule]')];
      if (!this._rail || !this._panels.length) return;
      this._desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._previous = this.querySelector('[data-collection-capsule-previous]');
      this._next = this.querySelector('[data-collection-capsule-next]');
      this._controls = this.querySelector('[data-collection-capsule-controls]');
      this._setActive(this.querySelector('[data-capsule-default]') || this._panels[0]);
      this._listen(this, 'pointermove', event => {
        if (!this._desktop.matches || event.pointerType === 'touch') return;
        if (this._pointerX === event.clientX && this._pointerY === event.clientY) return;
        this._pointerX = event.clientX;
        this._pointerY = event.clientY;
        const panel = event.target.closest('[data-collection-capsule]');
        if (panel && this.contains(panel)) this._setActive(panel);
      }, { passive: true });
      this._listen(this, 'focusin', event => {
        if (!this._desktop.matches) return;
        const panel = event.target.closest('[data-collection-capsule]');
        if (panel && this.contains(panel)) this._setActive(panel);
      });
      this._listen(this, 'shopify:block:select', event => {
        const panel = event.target.closest('[data-collection-capsule]');
        if (panel && this.contains(panel)) this._setActive(panel);
      });
      this._listen(this._rail, 'scroll', () => this._scheduleControls(), { passive: true });
      this._listen(window, 'resize', () => this._scheduleControls(), { passive: true });
      if (this._previous) this._listen(this._previous, 'click', () => this._step(-1));
      if (this._next) this._listen(this._next, 'click', () => this._step(1));
      if (this._controls) this._controls.hidden = this._panels.length < 2;
      this._scheduleControls();
    }

    disconnectedCallback() {
      this._generation = (this._generation || 0) + 1;
      this._initializing = false;
      this._connected = false;
      for (const [target, event, handler, options] of this._listeners || []) target.removeEventListener(event, handler, options);
      this._listeners = [];
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = 0;
      this._pointerX = this._pointerY = undefined;
      if (this._controls) this._controls.hidden = true;
    }

    _listen(target, event, handler, options) {
      target.addEventListener(event, handler, options);
      this._listeners.push([target, event, handler, options]);
    }

    _setActive(panel) {
      if (!panel || this._active === panel) return;
      this._active = panel;
      for (const item of this._panels) item.classList.toggle('is-open', item === panel);
    }

    _scheduleControls() {
      if (!this._connected || this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = 0;
        if (!this._connected) return;
        const maximum = Math.max(0, this._rail.scrollWidth - this._rail.clientWidth);
        if (this._previous) this._previous.disabled = this._rail.scrollLeft <= 2;
        if (this._next) this._next.disabled = this._rail.scrollLeft >= maximum - 2;
      });
    }

    _step(direction) {
      if (!this._connected || !this._rail) return;
      const current = this._rail.scrollLeft;
      const maximum = Math.max(0, this._rail.scrollWidth - this._rail.clientWidth);
      const offsets = this._panels.map(panel => panel.offsetLeft);
      const next = direction > 0
        ? offsets.find(offset => offset > current + 8)
        : offsets.slice().reverse().find(offset => offset < current - 8);
      const left = Math.max(0, Math.min(maximum, next === undefined ? (direction > 0 ? maximum : 0) : next));
      this._rail.scrollTo({ left, behavior: this._reduced.matches ? 'instant' : 'smooth' });
    }
  }

  customElements.define(name, GlintCollectionCapsules);
})();
